import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { TILE_PROVIDERS, MAX_ZOOM, TILE_SIZE, KEEP_BUFFER } from '../constants'

interface UseMapTilesProps {
  mapRef: React.MutableRefObject<L.Map | null>
  tileLayerRef: React.MutableRefObject<L.TileLayer | null>
  isInitializedRef: React.MutableRefObject<boolean>
  mapType: 'osm' | 'satellite' | 'terrain' | 'dark'
  tilesLoadingCountRef: React.MutableRefObject<number>
  setIsLoadingTiles: (loading: boolean) => void
  isLoadingTiles: boolean
  loadingTimeoutRef: React.MutableRefObject<number | null>
  userHasInteractedRef: React.MutableRefObject<boolean>
}

export const useMapTiles = ({
  mapRef,
  tileLayerRef,
  isInitializedRef,
  mapType,
  tilesLoadingCountRef,
  setIsLoadingTiles,
  isLoadingTiles,
  loadingTimeoutRef,
  userHasInteractedRef,
}: UseMapTilesProps) => {
  const prevMapTypeRef = useRef<string | undefined>(mapType)

  useEffect(() => {
    if (!mapRef.current || !isInitializedRef.current || !tileLayerRef.current) return
    if (!mapRef.current.getContainer()) return
    if (prevMapTypeRef.current === mapType) return
    
    prevMapTypeRef.current = mapType

    try {
      let currentCenter
      let currentZoom
      try {
        currentCenter = mapRef.current.getCenter()
        currentZoom = mapRef.current.getZoom()
      } catch (error) {
        console.warn('Map not ready for type switch:', error)
        return
      }

      const oldLayer = tileLayerRef.current
      mapRef.current.removeLayer(oldLayer)
      oldLayer.off()

      tilesLoadingCountRef.current = 0

      const tileUrl = TILE_PROVIDERS[mapType]
      const newTileLayer = L.tileLayer(tileUrl, {
        maxZoom: MAX_ZOOM,
        attribution: '© OpenStreetMap contributors',
        tileSize: TILE_SIZE,
        updateWhenZooming: false,
        updateWhenIdle: true,
        keepBuffer: KEEP_BUFFER,
      })

      const typeSwitchLoadCompleteRef = { current: false }

      const handleTypeLoading = () => {
        tilesLoadingCountRef.current++
        if (!typeSwitchLoadCompleteRef.current) {
          requestAnimationFrame(() => {
            setIsLoadingTiles(true)
          })
        }
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current)
        }
      }

      const handleTypeLoad = () => {
        tilesLoadingCountRef.current = Math.max(0, tilesLoadingCountRef.current - 1)
        if (tilesLoadingCountRef.current === 0 && !typeSwitchLoadCompleteRef.current) {
          typeSwitchLoadCompleteRef.current = true
          loadingTimeoutRef.current = window.setTimeout(() => {
            requestAnimationFrame(() => {
              setIsLoadingTiles(false)
            })
          }, 300)
        }
      }

      const handleTypeError = () => {
        tilesLoadingCountRef.current = Math.max(0, tilesLoadingCountRef.current - 1)
        if (tilesLoadingCountRef.current === 0 && !typeSwitchLoadCompleteRef.current) {
          typeSwitchLoadCompleteRef.current = true
          loadingTimeoutRef.current = window.setTimeout(() => {
            requestAnimationFrame(() => {
              setIsLoadingTiles(false)
            })
          }, 500)
        }
      }

      newTileLayer.on('loading', handleTypeLoading)
      newTileLayer.on('load', handleTypeLoad)
      newTileLayer.on('tileerror', handleTypeError)

      newTileLayer.addTo(mapRef.current)
      tileLayerRef.current = newTileLayer

      if (!isLoadingTiles) {
        requestAnimationFrame(() => {
          setIsLoadingTiles(true)
        })
      }

      if (currentCenter && currentZoom) {
        const isDefaultPosition =
          Math.abs(currentCenter.lat - 52.37) < 0.1 &&
          Math.abs(currentCenter.lng - 4.9) < 0.1

        if (!isDefaultPosition && !userHasInteractedRef.current) {
          mapRef.current.setView(currentCenter, currentZoom, {
            animate: false,
          })
        }

        if (mapRef.current) {
          requestAnimationFrame(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize(false)
            }
          })
        }
      }
    } catch (error) {
      console.warn('Error switching map type:', error)
      setIsLoadingTiles(false)
    }
  }, [mapType, mapRef, tileLayerRef, isInitializedRef, tilesLoadingCountRef, setIsLoadingTiles, isLoadingTiles, loadingTimeoutRef, userHasInteractedRef])
}
